import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Save, RefreshCw, Send, Pencil } from "lucide-react";
import AdminSidebar from "../../components/admin/AdminSidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}` });

const BLANK_RECIP = {
  name: "", email: "", phone: "",
  receive_rental_email: true, receive_rental_sms: false,
  receive_trial_email: true, receive_trial_sms: false,
  active: true,
};

// AU mobile/landline + intl format
const AU_PHONE_RE = /^(\+61\s?[2-478](\s?\d){8}|0[2-478](\s?\d){8})$/;
function isValidAuPhone(p) {
  const s = (p || "").replace(/\s+/g, "");
  if (!s) return false;
  return AU_PHONE_RE.test(s);
}
function isValidEmail(e) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim()); }

function applyTokens(text, ctx) {
  if (!text) return "";
  const map = {
    "[Customer Name]": ctx.customer_name || "",
    "[Outfit Name]": ctx.outfit_name || "",
    "[Event Date]": ctx.event_date_short || "",
    "[Date]": ctx.date_short || "",
    "[Time]": ctx.time || "",
    "[Rental Period]": ctx.rental_period || "",
    "[Delivery/Pickup]": ctx.delivery_method || "",
    "[Booking ID]": ctx.booking_id || "",
    "[Trial Lead ID]": ctx.trial_lead_id || "",
  };
  let out = text;
  Object.entries(map).forEach(([k, v]) => { out = out.split(k).join(String(v)); });
  return out;
}

function shortDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
  } catch { return iso; }
}

export default function AdminNotifications() {
  const [tab, setTab] = useState("recipients");
  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <AdminSidebar />
      <main className="lg:ml-64 p-6 md:p-10" data-testid="admin-notifications-page">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-2xl font-semibold text-[#282828] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Notifications</h1>
          <p className="text-[12px] text-[#676664]/70 mb-5">
            Manage notification recipients, send manual follow-up emails or SMS, and adjust messaging settings.
            <a href="/admin/communications?tab=templates" className="ml-2 underline text-[#9A7B4F]">Templates have moved to Communications →</a>
          </p>

          <div className="flex gap-1 mb-6 border-b border-[#282828]/10 flex-wrap">
            {[
              { k: "recipients", l: "Recipients" },
              { k: "email", l: "Send Email" },
              { k: "sms", l: "Send SMS" },
              { k: "settings", l: "Settings" },
            ].map(t => (
              <button key={t.k} onClick={() => setTab(t.k)}
                data-testid={`tab-${t.k}`}
                className={`px-4 py-2 text-[12px] tracking-wider uppercase border-b-2 transition-colors ${tab === t.k ? "border-[#9A7B4F] text-[#282828]" : "border-transparent text-[#676664]/60 hover:text-[#282828]"}`}>
                {t.l}
              </button>
            ))}
          </div>

          {tab === "recipients" && <RecipientsTab />}
          {tab === "email" && <SendMessageTab kind="email" />}
          {tab === "sms" && <SendMessageTab kind="sms" />}
          {tab === "settings" && <SettingsTab />}
        </div>
      </main>
    </div>
  );
}

// ─────────── Settings ───────────
function SettingsTab() {
  const [contact, setContact] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/notification-settings`, { headers: headers() });
        if (!cancelled) setContact(res.data.customer_contact_number || "");
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Failed to load settings");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/notification-settings`, { customer_contact_number: contact }, { headers: headers() });
      toast.success("Settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const [testing, setTesting] = useState(false);
  const sendTestSms = async () => {
    if (!contact.trim()) { toast.error("Set a contact number first, then save."); return; }
    setTesting(true);
    try {
      const res = await axios.post(`${API}/admin/follow-up/sms/send`, {
        to_phone: contact.trim(),
        body: "This is a test SMS from Mahra Admin.",
        recipient_name: "Admin Test",
        template_key: "admin_test_sms",
      }, { headers: headers() });
      if (res.data?.ok) toast.success("Test SMS sent — check your phone & Communications log");
      else toast.error(res.data?.error || "Test SMS failed — see Communications log");
    } catch (e) { toast.error(e.response?.data?.detail || "Test SMS failed"); }
    setTesting(false);
  };

  if (loading) return <p className="text-[12px] text-[#676664]/60">Loading…</p>;

  return (
    <div className="bg-white border border-[#282828]/8 p-5 max-w-xl" data-testid="settings-tab">
      <h3 className="text-[14px] font-semibold mb-1">Customer-facing contact number</h3>
      <p className="text-[11px] text-[#676664]/60 mb-3">
        This number is inserted into the <code className="bg-[#FAF8F0] px-1">[Contact Number]</code> token in customer SMS templates,
        so customers know where to reach you if they can&apos;t reply to ClickSend.
      </p>
      <label className="text-[10px] uppercase tracking-wider text-[#676664]/60 block mb-1">Contact number</label>
      <input value={contact} onChange={e => setContact(e.target.value)} placeholder="0404 425 381"
        className="w-full max-w-md border border-[#282828]/12 px-3 py-2 text-[13px] mb-3" data-testid="contact-number-input" />
      <div className="flex flex-wrap gap-2">
        <button onClick={save} disabled={saving} data-testid="save-settings-btn"
          className="flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-wider bg-[#9A7B4F] text-white hover:bg-[#8B6B3F] disabled:opacity-50">
          {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save
        </button>
        <button onClick={sendTestSms} disabled={testing || !contact.trim()} data-testid="test-sms-btn"
          className="flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-wider border border-[#282828]/12 text-[#282828] bg-white hover:bg-[#FAF8F0] disabled:opacity-50"
          title="Sends 'This is a test SMS from Mahra Admin.' to the contact number above">
          {testing ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Send test SMS to myself
        </button>
      </div>
    </div>
  );
}

// ─────────── Recipient editor modal (shared by Add + Edit) ───────────
function RecipientModal({ initial, onClose, onSaved }) {
  const isEdit = Boolean(initial?.id);
  const [draft, setDraft] = useState(() => ({ ...BLANK_RECIP, ...(initial || {}) }));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!draft.name?.trim()) e.name = "Name is required";

    const wantsEmail = draft.receive_rental_email || draft.receive_trial_email;
    const wantsSms = draft.receive_rental_sms || draft.receive_trial_sms;

    if (wantsEmail && !draft.email?.trim()) e.email = "Email is required when an email channel is enabled";
    if (draft.email?.trim() && !isValidEmail(draft.email)) e.email = "Enter a valid email address";
    if (wantsSms && !draft.phone?.trim()) e.phone = "Phone is required when an SMS channel is enabled";
    if (draft.phone?.trim() && !isValidAuPhone(draft.phone)) e.phone = "Use 04XX XXX XXX or +61 4XX XXX XXX";
    if (!draft.email?.trim() && !draft.phone?.trim()) e.email = e.email || "Recipient needs at least an email or a phone number";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async () => {
    if (!validate()) { toast.error("Please fix the highlighted fields"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.patch(`${API}/admin/notification-recipients/${initial.id}`, draft, { headers: headers() });
        toast.success("Recipient updated");
      } else {
        await axios.post(`${API}/admin/notification-recipients`, draft, { headers: headers() });
        toast.success("Recipient added");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full p-5 overflow-y-auto max-h-[90vh]" onClick={e => e.stopPropagation()} data-testid="recipient-modal">
        <h3 className="text-[16px] font-semibold mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          {isEdit ? "Edit recipient" : "Add recipient"}
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#676664]/60 block mb-1">Name *</label>
            <input value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
              className={`w-full border px-3 py-2 text-[13px] ${errors.name ? "border-[#B85C5C]" : "border-[#282828]/12"}`} data-testid="recipient-name" />
            {errors.name && <p className="text-[11px] text-[#B85C5C] mt-1">{errors.name}</p>}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#676664]/60 block mb-1">Email</label>
            <input type="email" value={draft.email} onChange={e => setDraft(d => ({ ...d, email: e.target.value }))}
              className={`w-full border px-3 py-2 text-[13px] ${errors.email ? "border-[#B85C5C]" : "border-[#282828]/12"}`} data-testid="recipient-email" />
            {errors.email && <p className="text-[11px] text-[#B85C5C] mt-1">{errors.email}</p>}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#676664]/60 block mb-1">Phone</label>
            <input value={draft.phone} placeholder="0404 425 381" onChange={e => setDraft(d => ({ ...d, phone: e.target.value }))}
              className={`w-full border px-3 py-2 text-[13px] ${errors.phone ? "border-[#B85C5C]" : "border-[#282828]/12"}`} data-testid="recipient-phone" />
            {errors.phone && <p className="text-[11px] text-[#B85C5C] mt-1">{errors.phone}</p>}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[12px] mb-3">
          {[
            ["receive_rental_email", "Rental Email"],
            ["receive_rental_sms", "Rental SMS"],
            ["receive_trial_email", "Trial Email"],
            ["receive_trial_sms", "Trial SMS"],
          ].map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!draft[k]} onChange={e => setDraft(d => ({ ...d, [k]: e.target.checked }))}
                data-testid={`recipient-${k}`} />
              <span>{label}</span>
            </label>
          ))}
        </div>

        <label className="flex items-center gap-2 text-[12px] mb-5 cursor-pointer">
          <input type="checkbox" checked={!!draft.active} onChange={e => setDraft(d => ({ ...d, active: e.target.checked }))} data-testid="recipient-active" />
          <span>Active</span>
        </label>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-[11px] uppercase tracking-wider border border-[#282828]/12 text-[#676664]">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="save-recipient-btn"
            className="flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-wider bg-[#9A7B4F] text-white hover:bg-[#8B6B3F] disabled:opacity-50">
            {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {isEdit ? "Save changes" : "Add recipient"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────── Recipients tab ───────────
function RecipientsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { initial } | null

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/notification-recipients`, { headers: headers() });
      setRows(res.data.recipients || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load recipients");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/notification-recipients`, { headers: headers() });
        if (!cancelled) setRows(res.data.recipients || []);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Failed to load recipients");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const togglePatch = async (id, patch) => {
    try {
      await axios.patch(`${API}/admin/notification-recipients/${id}`, patch, { headers: headers() });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this recipient?")) return;
    try {
      await axios.delete(`${API}/admin/notification-recipients/${id}`, { headers: headers() });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  if (loading) return <p className="text-[12px] text-[#676664]/60">Loading…</p>;

  return (
    <div data-testid="recipients-tab">
      <div className="flex justify-between items-center mb-3">
        <p className="text-[12px] text-[#676664]/70">{rows.length} recipient{rows.length === 1 ? "" : "s"}</p>
        <button onClick={() => setModal({ initial: BLANK_RECIP })} data-testid="add-recipient-btn"
          className="flex items-center gap-2 px-3 py-2 text-[11px] tracking-wider uppercase bg-[#9A7B4F] text-white hover:bg-[#8B6B3F]">
          <Plus className="w-3.5 h-3.5" /> Add Recipient
        </button>
      </div>

      <div className="overflow-x-auto bg-white border border-[#282828]/8">
        <table className="w-full text-[12px] border-collapse">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#676664]/60 border-b border-[#282828]/10">
              <th className="p-2">Name</th><th className="p-2">Email</th><th className="p-2">Phone</th>
              <th className="p-2 text-center">Rental Email</th><th className="p-2 text-center">Rental SMS</th>
              <th className="p-2 text-center">Trial Email</th><th className="p-2 text-center">Trial SMS</th>
              <th className="p-2 text-center">Active</th><th className="p-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} data-testid={`recipient-row-${r.id}`} className="border-b border-[#282828]/6">
                <td className="p-2">{r.name}</td>
                <td className="p-2 text-[#676664]">{r.email || "—"}</td>
                <td className="p-2 text-[#676664]">{r.phone || "—"}</td>
                {["receive_rental_email", "receive_rental_sms", "receive_trial_email", "receive_trial_sms", "active"].map(k => (
                  <td key={k} className="p-2 text-center">
                    <input type="checkbox" data-testid={`toggle-${k}-${r.id}`} checked={!!r[k]} onChange={e => togglePatch(r.id, { [k]: e.target.checked })} />
                  </td>
                ))}
                <td className="p-2 text-right whitespace-nowrap">
                  <button onClick={() => setModal({ initial: r })} data-testid={`edit-recipient-${r.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider border border-[#282828]/12 text-[#676664] hover:bg-[#FAF8F0] mr-2">
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  <button onClick={() => remove(r.id)} data-testid={`delete-recipient-${r.id}`}
                    className="text-[#B85C5C] hover:opacity-70 align-middle">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={9} className="p-6 text-center text-[#676664]/50 italic">No recipients yet. Add one to start receiving booking &amp; trial alerts.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modal && (
        <RecipientModal
          initial={modal.initial}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}

// ─────────── Send Email / Send SMS ───────────
function SendMessageTab({ kind }) {
  const isEmail = kind === "email";
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [toEmail, setToEmail] = useState("");
  const [toPhone, setToPhone] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [outfitName, setOutfitName] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/${isEmail ? "email" : "sms"}-templates`, { headers: headers() });
        if (!cancelled) setTemplates(res.data.templates || []);
      } catch { if (!cancelled) toast.error("Failed to load templates"); }
    })();
    return () => { cancelled = true; };
  }, [isEmail]);

  const ctx = useMemo(() => ({
    customer_name: recipientName,
    outfit_name: outfitName,
    event_date_short: shortDate(eventDate),
    date_short: shortDate(eventDate),
    time: "",
    delivery_method: "",
    rental_period: "",
  }), [recipientName, outfitName, eventDate]);

  const applyTemplate = (id) => {
    setTplId(id);
    const tpl = templates.find(t => t.id === id);
    if (!tpl) { setSubject(""); setBody(""); return; }
    if (isEmail) setSubject(applyTokens(tpl.subject || "", ctx));
    setBody(applyTokens(tpl.body || "", ctx));
  };

  const send = async () => {
    if (isEmail) {
      if (!toEmail.trim()) { toast.error("Recipient email required"); return; }
      if (!subject.trim() || !body.trim()) { toast.error("Subject and body required"); return; }
    } else {
      if (!toPhone.trim()) { toast.error("Recipient phone required"); return; }
      if (!body.trim()) { toast.error("Message body required"); return; }
    }
    setSending(true);
    try {
      const url = `${API}/admin/follow-up/${isEmail ? "email" : "sms"}/send`;
      const payload = isEmail
        ? { to_email: toEmail.trim(), subject: subject, body: body, recipient_name: recipientName, template_key: templates.find(t => t.id === tplId)?.key || "" }
        : { to_phone: toPhone.trim(), body: body, recipient_name: recipientName, template_key: templates.find(t => t.id === tplId)?.key || "" };
      const res = await axios.post(url, payload, { headers: headers() });
      if (res.data?.ok) toast.success(isEmail ? "Email sent" : "SMS sent");
      else toast.error(res.data?.error || "Send failed — see Communications log for details");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
    setSending(false);
  };

  return (
    <div data-testid={`send-${kind}-tab`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Recipient Name</label>
          <input value={recipientName} onChange={e => setRecipientName(e.target.value)}
            className="w-full border border-[#282828]/12 px-3 py-2 text-[13px]" data-testid={`${kind}-recipient-name`} />
        </div>
        {isEmail ? (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Recipient Email *</label>
            <input type="email" value={toEmail} onChange={e => setToEmail(e.target.value)}
              className="w-full border border-[#282828]/12 px-3 py-2 text-[13px]" data-testid="email-to" />
          </div>
        ) : (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Recipient Phone *</label>
            <input type="tel" value={toPhone} onChange={e => setToPhone(e.target.value)} placeholder="04XX XXX XXX"
              className="w-full border border-[#282828]/12 px-3 py-2 text-[13px]" data-testid="sms-to" />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Template</label>
          <select value={tplId} onChange={e => applyTemplate(e.target.value)} data-testid={`${kind}-template-select`}
            className="w-full border border-[#282828]/12 px-3 py-2 text-[13px] bg-white">
            <option value="">— Start blank —</option>
            {templates.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Outfit (for [Outfit Name])</label>
          <input value={outfitName} onChange={e => setOutfitName(e.target.value)}
            className="w-full border border-[#282828]/12 px-3 py-2 text-[13px]" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Event Date (for [Event Date] / [Date])</label>
          <input type="date" value={eventDate} onChange={e => setEventDate(e.target.value)}
            className="w-full border border-[#282828]/12 px-3 py-2 text-[13px]" />
        </div>
      </div>

      {isEmail && (
        <div className="mb-3">
          <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Subject *</label>
          <input value={subject} onChange={e => setSubject(e.target.value)}
            className="w-full border border-[#282828]/12 px-3 py-2 text-[13px]" data-testid="email-subject" />
        </div>
      )}

      <div className="mb-3">
        <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">{isEmail ? "Body *" : "Message *"}</label>
        <textarea rows={isEmail ? 10 : 5} value={body} onChange={e => setBody(e.target.value)}
          className="w-full border border-[#282828]/12 px-3 py-2 text-[13px] font-mono resize-y" data-testid={`${kind}-body`} />
        {!isEmail && <p className="text-[10px] text-[#676664]/50 mt-1">{body.length}/160 chars — multi-part SMS may be sent if over 160.</p>}
      </div>

      <div className="flex justify-end">
        <button onClick={send} disabled={sending} data-testid={`send-${kind}-btn`}
          className="flex items-center gap-2 px-4 py-2.5 text-[12px] tracking-wider uppercase bg-[#9A7B4F] text-white hover:bg-[#8B6B3F] disabled:opacity-50">
          {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send {isEmail ? "Email" : "SMS"}
        </button>
      </div>
    </div>
  );
}

